/* The persistent "mentions of me" timeline (real-only).

   Unlike the inbox (notify.ts) — a short, in-memory feed of live presence
   signals — this is the durable record of every @-mention of you, kept apart
   by source (chat vs. tickets) and surviving reloads via account data. Chat
   mentions are recorded by notify.onMessage (which already rides every message
   and owns the chat toast/sound); ticket-comment mentions are detected here off
   the sync stream across ALL boards, and fire their own toast + sound. */
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'
import { sync } from '@/lib/sync'
import { useFloor } from '@/stores/floor'
import { useTasks } from '@/stores/tasks'
import { usePresence } from '@/stores/presence'
import { useToasts } from '@/stores/toasts'
import { playSfx } from '@/lib/sfx'
import { mentionsInclude, ticketVerb } from '@/lib/mentions'
import { localpart } from '@/lib/util'
import { MatrixError } from '@/types/matrix'
import type { MatrixEvent, RoomMessageContent } from '@/types/matrix'
import type { MentionItem, MentionSource, MentionAction } from '@/types/atrium'

const STORE = 'app.atrium.mentions'
const CAP = 150 // most recent kept; account_data is ~100KB/type
const SNIP = 280 // chars of context stored per item
const SCAN_PAGES = 10 // max message-filtered pages/room on the offline catch-up scan (×100 msgs)

function snippet(t: string): string {
  const s = String(t || '').replace(/\s+/g, ' ').trim()
  return s.length > SNIP ? s.slice(0, SNIP - 1) + '…' : s
}

interface MentionsState {
  items: MentionItem[]
  /** Watermark (ms): chat history at/under this ts has already been scanned for
   *  mentions, so the boot catch-up only fetches what arrived while we were away.
   *  0 = never scanned (first run starts the clock now — no pre-deploy history). */
  chatScan: number
  inited: boolean
  loaded: boolean
  scanning: boolean
  saveTimer: ReturnType<typeof setTimeout> | null
}

const byNewest = (a: MentionItem, b: MentionItem): number => b.ts - a.ts

export const useMentions = defineStore('mentions', {
  state: (): MentionsState => ({ items: [], chatScan: 0, inited: false, loaded: false, scanning: false, saveTimer: null }),

  getters: {
    chat: (s): MentionItem[] => s.items.filter((m) => m.source === 'chat').sort(byNewest),
    tickets: (s): MentionItem[] => s.items.filter((m) => m.source === 'ticket').sort(byNewest),
    unread: (s): number => s.items.reduce((n, m) => n + (m.read ? 0 : 1), 0),
    unreadChat: (s): number => s.items.reduce((n, m) => n + (!m.read && m.source === 'chat' ? 1 : 0), 0),
    unreadTicket: (s): number => s.items.reduce((n, m) => n + (!m.read && m.source === 'ticket' ? 1 : 0), 0),
  },

  actions: {
    init() {
      if (this.inited) return
      this.inited = true
      // Ticket mentions — comments AND descriptions — on every board we sync
      // (chat is handled by notify.onMessage, which already runs on each message
      // everywhere; plus catchUpChat() below for offline gaps).
      sync.on('custom', (roomId, ev) => this.onTicketEvent(roomId, ev))
    },

    /** Load the durable timeline from account data (once, on boot). Merges with
     *  anything already recorded live — boot races the sync stream, and a freshly
     *  arrived mention must not be clobbered by the stored snapshot. */
    async boot() {
      if (this.loaded) return
      try {
        const data = await matrix.getAccountData<{ items?: MentionItem[]; chatScan?: number }>(STORE)
        const stored = Array.isArray(data?.items) ? data!.items.filter((m) => m && m.id && m.source) : []
        if (stored.length) {
          const have = new Set(this.items.map((m) => m.id))
          const merged = [...this.items, ...stored.filter((m) => !have.has(m.id))]
          merged.sort(byNewest)
          this.items = merged.slice(0, CAP)
        }
        if (typeof data?.chatScan === 'number') this.chatScan = data.chatScan
      } catch { /* none yet */ }
      this.loaded = true
    },

    nameOf(mxid: string): string {
      return useFloor().people[mxid]?.short || localpart(mxid) || 'Someone'
    },

    /** Record one mention. `live` items toast + chime (unless heads-down) and are
     *  the only ones that make noise — boot/replay never re-alerts. Deduped by id. */
    record(item: Omit<MentionItem, 'read'>, live: boolean): boolean {
      if (!item.id) return false
      if (this.items.some((m) => m.id === item.id)) return false
      const full: MentionItem = { ...item, text: snippet(item.text), read: false }
      this.items.unshift(full)
      if (this.items.length > CAP) this.items.length = CAP
      if (live && !usePresence().isDnd) {
        playSfx('mention')
        useToasts().push(
          item.source === 'ticket'
            ? {
                icon: 'at', color: 'var(--gold-bright)',
                title: `${item.fromName} ${ticketVerb(item)}`,
                text: item.ticketTitle ? `“${item.ticketTitle}”` : 'a ticket',
                action: 'Open', ms: 6500,
                onAction: () => window.location.assign(`#/tasks/${item.board}/${item.ticket}`),
              }
            : {
                icon: 'at', color: 'var(--gold-bright)',
                title: `${item.fromName} mentioned you`,
                text: snippet(item.text),
                action: item.slug ? 'Open' : undefined, ms: 6500,
                onAction: item.slug ? () => window.location.assign('#/room/' + item.slug) : undefined,
              },
        )
      }
      this.persist()
      return true
    },

    /** Catch up on chat mentions that arrived while we were offline. Bounded:
     *  message-filtered pages per readable floor room, only back to the watermark
     *  — never historical before the first run. Silent (the rail badge + one
     *  summary toast carry it; no per-mention chime on boot).
     *
     *  The watermark only advances to `now` when the scan had no TRANSIENT
     *  failure, so a one-off network/5xx error retries next boot instead of
     *  burning the gap. A 403 (history we aren't entitled to read) is a structural
     *  skip and doesn't hold the watermark. Backward paging is capped at
     *  SCAN_PAGES, so an offline gap with >SCAN_PAGES×100 real messages in ONE
     *  room won't be fully caught here — live sync still delivers those mentions
     *  whenever the client is actually online. */
    async catchUpChat() {
      if (this.scanning) return
      await this.boot()
      const me = matrix.userId()
      if (!me) return
      const floor = useFloor()
      const now = Date.now()
      if (!this.chatScan) { this.chatScan = now; this.persist(); return } // first run: start the clock
      const since = this.chatScan
      this.scanning = true
      let found = 0
      let transientFailure = false
      try {
        const rooms = floor.rooms.filter((r) => r.roomId && (!r.team || floor.canWalkIn(r)))
        for (const r of rooms) {
          try {
            let from: string | undefined
            let done = false
            let pages = 0
            while (!done && pages++ < SCAN_PAGES) {
              const data = await matrix.messages(r.roomId, { dir: 'b', limit: 100, from, filter: { types: ['m.room.message'] } })
              for (const ev of data.chunk || []) {
                const ts = ev.origin_server_ts || 0
                if (ts <= since) { done = true; break } // dir:'b' is newest→oldest — the rest is older
                if (ev.sender === me || !ev.event_id) continue
                const content = ev.content as RoomMessageContent
                const body = content?.body || ''
                const mentioned = content?.['m.mentions']?.user_ids?.includes(me) || mentionsInclude(body, me)
                if (!mentioned) continue
                if (this.record({
                  id: ev.event_id, source: 'chat', from: ev.sender || '', fromName: this.nameOf(ev.sender || ''),
                  text: body, ts, roomId: r.roomId, slug: floor.slugOf(r.roomId), roomName: r.name,
                }, false)) found++
              }
              if (!data.end || data.end === from || !data.chunk?.length) done = true
              from = data.end
            }
          } catch (e) {
            // 403 = history we aren't entitled to (e.g. a team room we're not in) —
            // nothing readable to lose, so don't hold the watermark for it. Anything
            // else is transient: keep the watermark so the next boot retries the gap.
            if (!(e instanceof MatrixError && e.httpStatus === 403)) transientFailure = true
          }
        }
        if (!transientFailure) this.chatScan = now
        this.persist()
        if (found && !usePresence().isDnd) {
          useToasts().push({
            icon: 'at', color: 'var(--gold-bright)',
            title: `${found} mention${found > 1 ? 's' : ''} while you were away`,
            text: 'Catch up in Mentions.', action: 'Open', ms: 7000,
            onAction: () => window.location.assign('#/mentions'),
          })
        }
      } finally {
        this.scanning = false
      }
    },

    /** A ticket comment or ticket change landed on some board — record it if it
     *  concerns me, either because it @-mentions me or because I'm subscribed
     *  (author / assignee / ever-mentioned). The Conductor denormalizes the
     *  recipients onto the event (`mentions` = freshly mentioned, `new_assignees`
     *  = freshly assigned, `subscribers` = the notifiable set minus anyone muted),
     *  so we never re-parse Markdown and a muted task is already filtered out.
     *
     *  Loudness is selective (toast + chime only): being mentioned, being newly
     *  assigned, or a new comment. Column moves / field edits are recorded
     *  silently — they surface via the unread badge in the Mentions view. */
    onTicketEvent(roomId: string, ev: MatrixEvent) {
      if (!ev || !ev.event_id) return
      const c = (ev.content || {}) as Record<string, any>
      const me = matrix.userId()
      if (!me) return
      if (c.by === me) return // your own change never notifies you
      const ts = c.ts || ev.origin_server_ts || Date.now()
      // The Conductor's `subscribers` IS the notifiable set: it already includes
      // fresh mentions/assignees and already excludes anyone who muted the task.
      // So gating on membership here gives comprehensive mute for free — a fresh
      // @-mention never pierces a mute, because a muted person isn't in the set.
      const subscribed = Array.isArray(c.subscribers) && c.subscribers.includes(me)

      if (ev.type === 'app.atrium.ticket.comment') {
        if (!c.ticket || !c.body || c.replaces) return // edits/redactions aren't new activity
        if (!subscribed) return
        const mentioned = (Array.isArray(c.mentions) && c.mentions.includes(me)) || mentionsInclude(c.body, me)
        const board = useTasks().boardIdOf(roomId, c.board || '')
        if (!board) return
        // A comment is high-signal either way (mention or a thread you follow).
        this.record({
          id: ev.event_id, source: 'ticket' as MentionSource,
          kind: mentioned ? 'mention' : 'sub', action: mentioned ? undefined : 'commented',
          from: c.by || '', fromName: this.nameOf(c.by || ''),
          text: c.body, ts, board, ticket: c.ticket,
          ticketTitle: useTasks().tickets.find((t) => t.id === c.ticket)?.title,
        }, true)
        return
      }

      if (ev.type === 'app.atrium.ticket.event') {
        // 'commented' rides its own ticket.comment event (handled above) — skip
        // the duplicate activity-log entry so comments never double-notify.
        if (!['created', 'updated', 'moved', 'transferred'].includes(c.action)) return
        if (!c.ticket || !subscribed) return
        const mentioned = Array.isArray(c.mentions) && c.mentions.includes(me)
        const assigned = Array.isArray(c.new_assignees) && c.new_assignees.includes(me)
        const board = useTasks().boardIdOf(roomId, c.board || '')
        if (!board) return
        // Mentions and assignments are loud; passive updates (moves, field edits)
        // are recorded silently (live=false → no toast/chime, just the badge).
        const kind: 'mention' | 'sub' = mentioned ? 'mention' : 'sub'
        const action: MentionAction | undefined = mentioned
          ? undefined
          : assigned ? 'assigned' : (c.action as MentionAction)
        const loud = mentioned || assigned
        this.record({
          id: ev.event_id, source: 'ticket' as MentionSource,
          kind, action,
          from: c.by || '', fromName: this.nameOf(c.by || ''),
          text: '', ts, board, ticket: c.ticket, ticketTitle: c.title,
        }, loud)
      }
    },

    markRead(source?: MentionSource) {
      let changed = false
      this.items.forEach((m) => {
        if ((!source || m.source === source) && !m.read) { m.read = true; changed = true }
      })
      if (changed) this.persist()
    },

    persist() {
      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(() => {
        matrix.setAccountData(STORE, { items: this.items.slice(0, CAP), chatScan: this.chatScan }).catch(() => {})
      }, 800)
    },
  },
})
